import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Printer, Thermometer, TrendingUp, AlertTriangle } from "lucide-react";
import { TemperatureChart, RANGES, downloadTemperatureExport } from "@/components/TemperatureTrend";

const Card = ({ label, value, tone = "text-slate-900", testid }) => (
  <div className="border border-slate-200 bg-white p-3" data-testid={testid}>
    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`num text-2xl font-bold ${tone}`}>{value}</div>
  </div>
);

export default function TemperatureTrends() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [onlyProblems, setOnlyProblems] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/equipment/temperature-trends", { params: { days } });
      setData(res.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-slate-500" data-testid="temp-trends-loading">Loading temperature trends…</div>;

  const shown = onlyProblems
    ? data.instruments.filter((g) => g.stats.out_of_range > 0 || g.stats.drifting)
    : data.instruments;

  return (
    <div className="space-y-5" data-testid="temperature-trends-page">
      <div>
        <Link to="/equipment" className="inline-flex items-center gap-1 text-sm text-[#134e4a] hover:underline"
              data-testid="back-to-equipment">
          <ArrowLeft size={14} /> Back to TechTraX
        </Link>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
              Temperature Trends
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Every logged reading across the lab, plotted against its acceptable range with a{" "}
              {data.moving_average_days}-day moving average to expose slow drift.
            </p>
          </div>
          <div className="no-print flex flex-wrap items-center gap-2">
            <div className="flex border border-slate-300">
              {RANGES.map((d) => (
                <button key={d} type="button" onClick={() => setDays(d)}
                        data-testid={`trends-range-${d}`}
                        className={`px-3 py-2 text-xs font-semibold transition-colors ${
                          days === d ? "bg-[#134e4a] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
                  {d === 365 ? "1 year" : `${d} days`}
                </button>
              ))}
            </div>
            <Button variant="outline" className="h-11 border-slate-300" data-testid="trends-export-csv"
                    onClick={() => downloadTemperatureExport("csv", days, "", "all")}>
              <Download size={15} className="mr-2" /> CSV
            </Button>
            <Button variant="outline" className="h-11 border-slate-300" data-testid="trends-export-pdf"
                    onClick={() => downloadTemperatureExport("pdf", days, "", "all")}>
              <Printer size={15} className="mr-2" /> PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Card label="Instruments" value={data.totals.instruments} testid="trends-card-instruments" />
        <Card label="Readings" value={data.totals.readings} testid="trends-card-readings" />
        <Card label="Out of range" value={data.totals.out_of_range}
              tone={data.totals.out_of_range ? "text-red-800" : "text-slate-900"} testid="trends-card-oor" />
        <Card label="Drifting" value={data.totals.drifting}
              tone={data.totals.drifting ? "text-amber-700" : "text-slate-900"} testid="trends-card-drifting" />
        <Card label="No readings" value={data.totals.no_readings}
              tone={data.totals.no_readings ? "text-amber-700" : "text-slate-900"} testid="trends-card-none" />
      </div>

      <label className="no-print flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)}
               data-testid="trends-only-problems" className="h-4 w-4" />
        Show only instruments that went out of range or are drifting
      </label>

      {shown.length === 0 ? (
        <p className="border border-slate-200 bg-slate-50 p-6 text-sm text-slate-500" data-testid="trends-empty">
          {onlyProblems ? "Nothing out of range or drifting in this period — good news."
            : "No instruments record temperatures yet. Flag a maintenance task as “records temperature” to start plotting."}
        </p>
      ) : shown.map((g) => (
        <section key={g.equipment_id} className="border border-slate-200"
                 data-testid={`trend-card-${g.equipment_id}`}>
          <header className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
            <Thermometer size={15} className="text-[#134e4a]" />
            <Link to={`/equipment/${g.equipment_id}`}
                  className="text-base font-semibold text-slate-800 hover:underline md:text-lg">
              {g.equipment_name}
            </Link>
            <span className="text-xs text-slate-500">
              {g.equipment_type}{g.location ? ` · ${g.location}` : ""}
            </span>
            {g.stats.out_of_range > 0 && (
              <span className="inline-flex items-center gap-1 bg-red-800 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                <AlertTriangle size={11} /> {g.stats.out_of_range} out of range
              </span>
            )}
            {g.stats.drifting && (
              <span className="inline-flex items-center gap-1 bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900">
                <TrendingUp size={11} /> drifting
              </span>
            )}
          </header>
          <div className="p-3">
            <TemperatureChart group={g} compact />
          </div>
        </section>
      ))}
    </div>
  );
}
